import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { api, fileUrl, thumbUrl, videoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { StarRating } from "@/components/StarRating";
import { SaveToCollection } from "@/components/SaveToCollection";
import { X, MapPin, Loader2, Inbox, ChevronLeft, Sparkles, Star, RotateCcw, Search } from "lucide-react";

const LENS_SIZE = 200, LENS_ZOOM = 2.6, LONG_PRESS_MS = 320;

const SwipeCard = ({ item, onDecided, onRateRequest, front }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-250, 250], [-14, 14]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const nopeOpacity = useTransform(x, [-140, -40], [1, 0]);
  const rateOpacity = useTransform(y, [-140, -40], [1, 0]);

  const rootRef = useRef(null);
  const imgRef = useRef(null);
  const pressTimer = useRef(null);
  const startPt = useRef(null);
  const [magnify, setMagnify] = useState(false);
  const [lens, setLens] = useState(null);

  const isVideo = item.media_type === "video";
  const src = isVideo ? videoUrl(item) : thumbUrl(item.file_ref, 1200);

  const fling = (dir) => {
    animate(x, dir * 700, { type: "tween", duration: 0.32 });
    setTimeout(() => onDecided(dir > 0 ? "later" : "archive"), 200);
  };
  const onDragEnd = (_e, info) => {
    const { offset, velocity } = info;
    if (offset.y < -120 && Math.abs(offset.y) > Math.abs(offset.x)) {
      animate(x, 0, { type: "spring", stiffness: 300, damping: 25 });
      animate(y, 0, { type: "spring", stiffness: 300, damping: 25 });
      onRateRequest();
      return;
    }
    if (offset.x > 110 || velocity.x > 700) fling(1);
    else if (offset.x < -110 || velocity.x < -700) fling(-1);
    else { animate(x, 0, { type: "spring", stiffness: 300, damping: 25 }); animate(y, 0, { type: "spring", stiffness: 300, damping: 25 }); }
  };

  // ----- Long-press magnifier -----
  const computeLens = (clientX, clientY) => {
    const img = imgRef.current, root = rootRef.current;
    if (!img || !root || !img.naturalWidth) return null;
    const r = img.getBoundingClientRect();
    const rr = root.getBoundingClientRect();
    // object-contain letterboxes the photo; find the real displayed rect.
    const natRatio = img.naturalWidth / img.naturalHeight;
    const boxRatio = r.width / r.height;
    let dispW, dispH, offX, offY;
    if (natRatio > boxRatio) { dispW = r.width; dispH = r.width / natRatio; offX = 0; offY = (r.height - dispH) / 2; }
    else { dispH = r.height; dispW = r.height * natRatio; offY = 0; offX = (r.width - dispW) / 2; }
    const px = clientX - r.left, py = clientY - r.top;
    const cx = px - offX, cy = py - offY;
    if (cx < 0 || cy < 0 || cx > dispW || cy > dispH) return null;
    return {
      left: (r.left - rr.left) + px - LENS_SIZE / 2,
      top: (r.top - rr.top) + py - LENS_SIZE / 2,
      bgSize: `${dispW * LENS_ZOOM}px ${dispH * LENS_ZOOM}px`,
      bgPos: `${-(cx * LENS_ZOOM - LENS_SIZE / 2)}px ${-(cy * LENS_ZOOM - LENS_SIZE / 2)}px`,
    };
  };
  const clearPress = () => { if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; } };
  const onPointerDown = (e) => {
    if (!front || isVideo) return;
    startPt.current = { x: e.clientX, y: e.clientY };
    clearPress();
    const cx = e.clientX, cy = e.clientY;
    pressTimer.current = setTimeout(() => { setMagnify(true); setLens(computeLens(cx, cy)); }, LONG_PRESS_MS);
  };
  const onPointerMove = (e) => {
    if (magnify) { setLens(computeLens(e.clientX, e.clientY)); return; }
    if (pressTimer.current && startPt.current) {
      if (Math.abs(e.clientX - startPt.current.x) > 16 || Math.abs(e.clientY - startPt.current.y) > 16) clearPress();
    }
  };
  const endPress = () => { clearPress(); if (magnify) { setMagnify(false); setLens(null); } };

  return (
    <motion.div
      ref={rootRef}
      data-testid="swipe-card"
      className="absolute inset-0 rounded-3xl overflow-hidden bg-black shadow-2xl select-none"
      style={{ ...(front ? { x, y, rotate } : { scale: 0.94, y: 16 }), WebkitTouchCallout: "none", touchAction: "none" }}
      drag={front && !magnify ? true : false}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.6}
      onDragStart={clearPress}
      onDragEnd={onDragEnd}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPress}
      onPointerCancel={endPress}
      onPointerLeave={endPress}
      whileTap={{ cursor: "grabbing" }}
    >
      {/* Blurred fill so widescreen/portrait photos are never cropped — full pic stays viewable */}
      {!isVideo && <div className="absolute inset-0 bg-center bg-cover scale-125 blur-2xl opacity-50 pointer-events-none" style={{ backgroundImage: `url(${src})` }} />}
      {isVideo ? (
        <video ref={imgRef} src={src} className="relative w-full h-full object-contain pointer-events-none bg-black" autoPlay muted loop playsInline preload="auto" />
      ) : (
        <img ref={imgRef} src={src} alt="" draggable={false} className="relative w-full h-full object-contain pointer-events-none" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/50 pointer-events-none" />

      {/* Top bar: BareFootage Original + location */}
      <div className="absolute top-0 left-0 right-0 p-4 flex flex-wrap items-center gap-2 pointer-events-none z-10">
        {!item.is_official && <span data-testid="community-badge" className="text-[10px] uppercase tracking-widest font-bold text-emerald-300 bg-black/55 backdrop-blur px-2.5 py-1 rounded-full">Community</span>}
        {item.location?.display && <span className="inline-flex items-center gap-1 text-white/90 text-xs bg-black/55 backdrop-blur px-2.5 py-1 rounded-full"><MapPin className="w-3 h-3" /> {item.location.city || item.location.display}</span>}
      </div>

      {front && (
        <>
          <motion.div style={{ opacity: likeOpacity }} className="absolute top-16 left-6 rotate-[-12deg] border-4 border-emerald-400 text-emerald-400 rounded-xl px-4 py-1.5 text-2xl font-black tracking-wider z-20">
            RATE LATER
          </motion.div>
          <motion.div style={{ opacity: nopeOpacity }} className="absolute top-16 right-6 rotate-[12deg] border-4 border-rose-500 text-rose-500 rounded-xl px-4 py-1.5 text-2xl font-black tracking-wider z-20">
            SKIP
          </motion.div>
          <motion.div style={{ opacity: rateOpacity }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 border-4 border-primary text-primary rounded-xl px-4 py-1.5 text-2xl font-black tracking-wider z-20">
            <Star className="w-6 h-6 fill-primary" /> RATE
          </motion.div>
        </>
      )}

      {/* Magnifier lens (long-press) */}
      {front && magnify && lens && (
        <div data-testid="swipe-magnifier-lens" className="pointer-events-none absolute rounded-full border-2 border-white/80 shadow-2xl z-30"
          style={{ width: LENS_SIZE, height: LENS_SIZE, left: lens.left, top: lens.top,
            backgroundImage: `url(${src})`, backgroundRepeat: "no-repeat", backgroundColor: "#000",
            backgroundSize: lens.bgSize, backgroundPosition: lens.bgPos }} />
      )}

      {/* Bottom: uploader + location (no filename) */}
      <div className="absolute bottom-0 left-0 right-0 p-5 pointer-events-none z-10">
        <div className="flex items-center gap-3 text-white/85 text-sm">
          <span className="font-medium">{item.is_official ? "BareFootage" : `@${item.uploader_name}`}</span>
          {item.location?.display && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {item.location.city || item.location.display}</span>}
        </div>
      </div>

      {front && !magnify && !isVideo && (
        <div className="pointer-events-none absolute bottom-16 left-1/2 -translate-x-1/2 flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-black/45 backdrop-blur border border-white/10 text-white/70 z-10">
          <Search className="w-3 h-3" /> Hold to zoom
        </div>
      )}
    </motion.div>
  );
};

const Swipe = () => {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [deck, setDeck] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inbox, setInbox] = useState(0);
  const [rating, setRating] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [typeF, setTypeF] = useState("all");
  const [ccF, setCcF] = useState("");
  const [countries, setCountries] = useState([]);
  const fetching = useRef(false);

  const fetchDeck = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    try {
      const params = { limit: 15 };
      if (typeF !== "all") params.type = typeF === "photos" ? "image" : "video";
      if (ccF) params.cc = ccF;
      const r = await api.get("/swipe/deck", { params });
      setDeck((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...r.data.filter((d) => !seen.has(d.id))];
      });
    } catch { /* ignore */ }
    fetching.current = false;
    setLoading(false);
  }, [typeF, ccF]);

  const loadInbox = useCallback(async () => {
    try { const r = await api.get("/me/rate-later"); setInbox(r.data.length); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchDeck(); loadInbox();
  }, [user, fetchDeck, loadInbox]);

  useEffect(() => { setDeck([]); setLoading(true); }, [typeF, ccF]);
  useEffect(() => {
    api.get("/leaderboards/locations", { params: { level: "country", limit: 100 } }).then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  // Preload the next few cards so swiping stays instant.
  useEffect(() => {
    deck.slice(1, 4).forEach((c) => {
      if (c && c.media_type !== "video") { const im = new Image(); im.src = thumbUrl(c.file_ref, 1200); }
    });
  }, [deck]);

  const decide = async (action) => {
    const card = deck[0];
    if (!card) return;
    setDeck((d) => d.slice(1));
    setLastAction({ card, action });
    if (action === "later") setInbox((n) => n + 1);
    try {
      await api.post("/swipe/action", { media_id: card.id, action });
    } catch {
      setDeck((d) => [card, ...d]);
      setLastAction(null);
      if (action === "later") setInbox((n) => Math.max(0, n - 1));
      toast.error("Couldn't save — try again");
    }
  };

  const undo = async () => {
    if (!lastAction) return;
    const { card, action } = lastAction;
    setLastAction(null);
    setDeck((d) => [card, ...d]);
    if (action === "later") setInbox((n) => Math.max(0, n - 1));
    try { await api.post("/swipe/undo", { media_id: card.id }); toast.success("Brought it back"); } catch { /* ignore */ }
  };

  useEffect(() => { if (user && !loading && deck.length <= 3) fetchDeck(); }, [deck.length, user, loading, fetchDeck]);

  const submitRate = async (value) => {
    const card = deck[0];
    if (!card) return;
    setRating(false);
    setDeck((d) => d.slice(1));
    try { await api.post(`/media/${card.id}/rate`, { value }); toast.success(`Rated ${value}/10`); }
    catch { toast.error("Couldn't rate"); }
  };

  if (authLoading) return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex flex-col items-center justify-center text-center px-8">
        <Sparkles className="w-10 h-10 text-primary mb-4" />
        <h1 className="font-heading text-2xl text-white mb-2">Swipe Mode</h1>
        <p className="text-white/60 mb-6 text-sm">{"Sign in to discover fresh feet — swipe right to save for rating, left to skip."}</p>
        <button data-testid="swipe-signin" onClick={login} className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 font-medium">Sign in</button>
        <button onClick={() => navigate("/browse")} className="mt-4 text-white/50 text-sm">Back to browse</button>
      </div>
    );
  }

  const top = deck[0];
  const next = deck[1];

  return (
    <div className="min-h-screen bg-[#0a0a0c] flex flex-col" data-testid="swipe-page">
      <div className="flex items-center justify-between px-4 py-3">
        <button data-testid="swipe-back" onClick={() => navigate("/browse")} className="text-white/70 hover:text-white flex items-center gap-1 text-sm"><ChevronLeft className="w-5 h-5" /> Browse</button>
        <span className="font-heading text-lg text-white">Swipe</span>
        <div className="flex items-center gap-3">
          {lastAction && (
            <button data-testid="swipe-undo" onClick={undo} className="text-white/70 hover:text-white flex items-center gap-1 text-sm" title="Undo last swipe">
              <RotateCcw className="w-4 h-4" /> Undo
            </button>
          )}
          <button data-testid="swipe-inbox" onClick={() => navigate("/rate-later")} className="relative text-white/70 hover:text-white flex items-center gap-1.5 text-sm">
            <Inbox className="w-5 h-5" />
            {inbox > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center">{inbox}</span>}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 px-4 pb-1 flex-wrap" data-testid="swipe-filters">
        {[["all", "All"], ["photos", "Photos"], ["videos", "Videos"]].map(([v, l]) => (
          <button key={v} data-testid={`swipe-filter-${v}`} onClick={() => setTypeF(v)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${typeF === v ? "bg-primary text-primary-foreground border-primary" : "border-white/15 text-white/60 hover:text-white"}`}>{l}</button>
        ))}
        <select data-testid="swipe-filter-country" value={ccF} onChange={(e) => setCcF(e.target.value)}
          className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/15 text-white/80 outline-none focus:border-primary">
          <option value="" className="bg-[#111]">Anywhere</option>
          {countries.map((c) => <option key={c.cc || c.key} value={c.cc} className="bg-[#111]">{c.name}</option>)}
        </select>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-2">
        <div className="relative w-full max-w-sm aspect-[3/4.4]">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : !top ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center rounded-3xl border border-white/10 bg-white/[0.02] px-8">
              <Sparkles className="w-9 h-9 text-primary mb-3" />
              <h3 className="font-heading text-xl text-white">{"You're all caught up!"}</h3>
              <p className="text-white/50 text-sm mt-2">{"You've seen everything new. Check your inbox to rate what you saved."}</p>
              <button onClick={() => navigate("/rate-later")} className="mt-5 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium">Go to inbox ({inbox})</button>
            </div>
          ) : (
            <>
              {next && <SwipeCard key={next.id} item={next} front={false} onDecided={() => {}} onRateRequest={() => {}} />}
              <SwipeCard key={top.id} item={top} front onDecided={decide} onRateRequest={() => setRating(true)} />
              <div className="absolute top-3 right-3 z-20" data-testid="swipe-save-wrap">
                <SaveToCollection mediaId={top.id} />
              </div>
              {rating && (
                <div data-testid="swipe-rate-overlay" className="absolute inset-x-0 bottom-0 z-30 bg-black/85 backdrop-blur-md rounded-b-3xl p-5 pt-6 flex flex-col items-center gap-3 animate-in slide-in-from-bottom">
                  <span className="text-white font-heading text-lg">Rate this — 1 to 10</span>
                  <StarRating value={0} onRate={submitRate} size={26} />
                  <button onClick={() => setRating(false)} className="text-white/50 text-sm mt-1">Cancel</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {top && !loading && (
        <div className="flex items-center justify-center gap-6 pb-8 pt-2">
          <button data-testid="swipe-skip" onClick={() => decide("archive")}
            className="w-16 h-16 rounded-full bg-white/5 border-2 border-rose-500/60 text-rose-400 flex items-center justify-center hover:bg-rose-500/10 active:scale-95 transition-all">
            <X className="w-7 h-7" />
          </button>
          <button data-testid="swipe-rate" onClick={() => setRating(true)}
            className="w-14 h-14 rounded-full bg-white/5 border-2 border-primary/60 text-primary flex items-center justify-center hover:bg-primary/10 active:scale-95 transition-all">
            <Star className="w-6 h-6" />
          </button>
          <button data-testid="swipe-later" onClick={() => decide("later")}
            className="w-16 h-16 rounded-full bg-white/5 border-2 border-emerald-400/60 flex items-center justify-center hover:bg-emerald-400/10 active:scale-95 transition-all">
            <span aria-hidden className="w-8 h-8 bg-emerald-400"
              style={{ WebkitMaskImage: "url(/logo-icon.png)", maskImage: "url(/logo-icon.png)", WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat", WebkitMaskSize: "contain", maskSize: "contain", WebkitMaskPosition: "center", maskPosition: "center" }} />
          </button>
        </div>
      )}
      <p className="text-center text-white/30 text-xs pb-6">← Skip · ↑ Rate · Rate later →</p>
    </div>
  );
};

export default Swipe;
